import React from 'react';
import { useTranslation } from 'react-i18next';

import type { ListParserDataMatch } from '../../services/types';

interface MatchesPanelProps {
  matches: ListParserDataMatch[];
  /** When the row was matched again (a run from before the table): not what the customer saw */
  rematchedAt?: string;
}

const BADGE_CLASSES: Record<string, string> = {
  EXACT_MATCH: 'bg-green-100 text-green-800',
  SUFFIX_MATCH: 'bg-blue-100 text-blue-800',
  REVISION_MATCH: 'bg-purple-100 text-purple-800',
  NOTE_MATCH: 'bg-amber-100 text-amber-800',
  DESCRIPTION_MATCH: 'bg-gray-200 text-gray-800',
};

/**
 * The UM parts one input row matched. Sticky to the left edge of the scrolling
 * table, so it stays in view however far right the match cell that opened it is.
 */
export const MatchesPanel: React.FC<MatchesPanelProps> = ({
  matches,
  rematchedAt,
}) => {
  const { t } = useTranslation();

  return (
    <div className="sticky left-0 max-w-3xl px-4 py-3 sm:px-6">
      <div className="rounded-lg border border-blue-200 bg-white shadow-sm overflow-hidden">
        <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 text-sm font-semibold text-blue-900">
          {t('listParserData.matchesTitle', { count: matches.length })}
        </div>
        {rematchedAt && (
          <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900">
            {t('listParserData.rematched', {
              date: new Date(rematchedAt).toLocaleDateString(),
            })}
          </div>
        )}
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="px-4 py-2 font-medium">
                {t('listParserData.columns.matchType')}
              </th>
              <th className="px-4 py-2 font-medium">
                {t('listParserData.matchUmPart')}
              </th>
              <th className="px-4 py-2 font-medium">
                {t('listParserData.matchUmDescription')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {matches.map((match) => (
              <tr key={`${match.matchType}#${match.umPart}`}>
                <td className="px-4 py-2 whitespace-nowrap">
                  <span
                    className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                      BADGE_CLASSES[match.matchType] ??
                      'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {t(`listParserData.matchTypes.${match.matchType}`, {
                      defaultValue: match.matchType,
                    })}
                  </span>
                </td>
                <td className="px-4 py-2 font-mono font-medium text-gray-900 whitespace-nowrap">
                  {match.umPart}
                </td>
                <td className="px-4 py-2 text-gray-700">
                  {match.umDescription}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
